import React, { useState, useEffect } from 'react';
import { dashboardApi, usersApi, clientsApi, auditsApi } from '../services/api';
import api from '../services/api';
import { User, useAuth } from '../contexts/AuthContext';
import UserManagement from './UserManagement';
import ClientFormComplete from './ClientFormComplete';
import ClientManagement from './ClientManagement';
import ClientBranchManager from './ClientBranchManager';
import TimeAlertNotifications from './TimeAlertNotifications';
import CreateAuditForm from './CreateAuditForm';
import OperatorDashboard from './OperatorDashboard';
import FinanzasPage from '../pages/FinanzasPage';
import FinanzasCompleto from './FinanzasCompleto';
import ColaGestionPage from '../pages/ColaGestionPage';
import MessagesEmailView from './MessagesEmailView';
import MessageNotification from './MessageNotification';
import FileManagement from './FileManagement';
import AuditManagement from './AuditManagement';
import AuditTracking from './AuditTracking';
import SupervisorAuditReview from './SupervisorAuditReview';
import SupervisorMyAudits from './SupervisorMyAudits';
import SupervisorDashboard from './SupervisorDashboard';
import ClientAuditsList from './ClientAuditsList';
import AuditoriasTranisitoSupervisor from './AuditoriasTranisitoSupervisor';
import WorkTimeWidget from './WorkTimeWidget';
import SueldoWidget from './SueldoWidget';
import SueldosManagement from './SueldosManagement';
import MiCuentaPage from '../pages/MiCuentaPage';
import CompanyConfigPanel from './CompanyConfigPanel';
import SupervisionAlertsPanel from './SupervisionAlertsPanel';
import PersonalTracking from './PersonalTracking';
import TestOperatorPermissions from './TestOperatorPermissions';

interface DashboardProps {
  user: User;
  onLogout: () => Promise<void>;
}

interface Stats {
  audits: {
    total: number;
    pending: number;
    inProgress: number;
    completed: number;
  };
  clients: {
    total: number;
    active: number;
  };
  users: {
    total: number;
    operators: number;
    supervisors: number;
  };
  finances: {
    totalRevenue: number;
    pendingPayments: number;
    balance: number;
  };
}

const Dashboard: React.FC<DashboardProps> = ({ user, onLogout }) => {
  const { forceCleanLogout } = useAuth();
  const [stats, setStats] = useState<Stats | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(
    user.role === 'operador' ? 'my-session' :
    user.role === 'cliente' ? (user.permissions?.esAdministradorGrupo ? 'overview' : 'audits') :
    (user.role === 'contable' || user.role === 'ejecutivo') ? 'finanzas' :
    'overview'
  );
  const [showClientForm, setShowClientForm] = useState(false);
  const [showAuditForm, setShowAuditForm] = useState(false);
  const [editingAuditId, setEditingAuditId] = useState<number | null>(null);
  const [highlightAuditId, setHighlightAuditId] = useState<number | null>(null);
  const [audits, setAudits] = useState([]);
  const [clientAudits, setClientAudits] = useState([]);
  const [clientBalance, setClientBalance] = useState({ saldo: 0, pendiente: 0 });
  const [messageRefresh, setMessageRefresh] = useState(0); // Para forzar actualización de notificaciones
  const [alerts, setAlerts] = useState(null); // Alertas del sistema
  const [dismissedAlerts, setDismissedAlerts] = useState(new Set()); // IDs de alertas dismisseadas
  const [financeData, setFinanceData] = useState(null); // Datos financieros
  const [error, setError] = useState('');
  const [msg, setMsg] = useState('');
  const [empresasAdministradas, setEmpresasAdministradas] = useState([]); // Datos de empresas para admin grupo
  const [vistaEmpresaEspecifica, setVistaEmpresaEspecifica] = useState(null); // ID empresa específica a mostrar
  const [empresaEnContexto, setEmpresaEnContexto] = useState(null); // Empresa seleccionada para navegación en otras pestañas

  // Función que se llama cuando se lee un mensaje
  const handleMessageRead = () => {
    setMessageRefresh(prev => prev + 1); // Incrementar para forzar actualización
  };

  const dismissAlert = (alertId) => {
    setDismissedAlerts(prev => new Set([...prev, alertId]));
  };

  // Función para reparar inconsistencias (solo admin)
  const repararInconsistencias = async () => {
    if (!confirm(`🔧 ADMIN: ¿Ejecutar reparación de inconsistencias de pagos?
    
⚠️ Esta función:
• Sincroniza estados de auditorías con ingresos registrados
• Marca auditorías como pagadas si hay ingreso registrado
• Es seguro ejecutar múltiples veces

¿Continuar?`)) {
      return;
    }

    try {
      setError(""); setMsg("");
      setLoading(true);
      
      const response = await api.post("/finanzas/repair-inconsistencias");
      
      if (response.data.success) {
        const { reparaciones } = response.data;
        
        if (reparaciones.length > 0) {
          let mensaje = `🔧 Reparación completada: ${reparaciones.length} inconsistencias corregidas:\n\n`;
          reparaciones.forEach(rep => {
            mensaje += `• ${rep.cliente}: ${rep.auditorias} auditorías sincronizadas ($${rep.monto})\n`;
          });
          
          setMsg(`✅ ${mensaje}`);
        } else {
          setMsg("✅ No se encontraron inconsistencias. El sistema está sincronizado.");
        }
        
        // Recargar datos
        loadDashboardData();
      }
      
    } catch (err) {
      setError(`Error reparando inconsistencias: ${err.response?.data?.error || err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Función para reparar usuarios (solo admin)
  const repararUsuarios = async () => {
    if (!confirm(`👤 ADMIN: ¿Ejecutar reparación de usuarios?
    
⚠️ Esta función:
• Asigna usernames a usuarios que no los tengan
• Garantiza que todos puedan hacer login con email o username  
• Es seguro ejecutar múltiples veces

¿Continuar?`)) {
      return;
    }

    try {
      setError(""); setMsg("");
      setLoading(true);
      
      const response = await api.post("/users/fix-usernames");
      
      if (response.data.success) {
        const { fixes } = response.data;
        
        if (fixes.length > 0) {
          let mensaje = `👤 Reparación de usuarios completada: ${fixes.length} usuarios reparados:\n\n`;
          fixes.forEach(fix => {
            mensaje += `• ${fix.email} → username: ${fix.newUsername}\n`;
          });
          
          setMsg(`✅ ${mensaje}`);
        } else {
          setMsg("✅ No se encontraron usuarios sin username. Todos los usuarios están correctos.");
        }
      }
      
    } catch (err) {
      setError(`Error reparando usuarios: ${err.response?.data?.error || err.message}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const cargarDatos = () => {
      loadDashboardData();
      if (user.role === 'admin' || user.role === 'supervisor') {
        loadAlerts(); // Cargar alertas para admin y supervisor
        loadFinanceData(); // Cargar datos financieros
      }
    };
    
    cargarDatos();
    // Actualizar cada 10 segundos para que se vean los cambios automáticamente
    // const interval = setInterval(cargarDatos, 10000);
    // return () => clearInterval(interval);
  }, [user]);
  
  useEffect(() => {
    if (user.role === 'operador' || user.role === 'supervisor' || user.role === 'admin') {
      loadAudits();
    }
    if (user.role === 'cliente') {
      // Ejecutar TODAS las cargas EN PARALELO para administradores de grupo
      if (user.permissions?.esAdministradorGrupo) {
        Promise.all([
          loadClientAudits(),
          loadClientBalance(),
          loadEmpresasAdministradas()
        ]);
      } else {
        // Para clientes normales, ejecutar en paralelo también
        Promise.all([
          loadClientAudits(),
          loadClientBalance()
        ]);
      }
    }
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      
      // Solo cargar estadísticas para admin y supervisor
      if (['admin', 'supervisor'].includes(user.role)) {
        const response = await dashboardApi.getStats();
        if (response.data.success) {
          setStats(response.data.data);
        }
      } else {
        // Para otros roles, no cargar estadísticas generales
        setStats(null);
      }
    } catch (error) {
      console.error('Error cargando dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadAudits = async () => {
    try {
      // Admin debe usar el endpoint de gestión para ver horas reales
      const response = user.role === 'admin'
        ? await auditsApi.getAllForManagement()
        : await auditsApi.getAll({ all: 'true' });

      if (response.data.success) {
        setAudits(response.data.data);
      }
    } catch (error) {
      console.error('Error cargando auditorías:', error);
    }
  };

  const loadClientAudits = async () => {
    try {
      const response = await clientsApi.getMyAudits();
      const auditorias = Array.isArray(response.data) ? response.data : [];
      setClientAudits(auditorias);
      
      // Calcular balance inmediatamente después de cargar auditorías
      if (auditorias.length > 0) {
        let deudaTotal = 0;
        let deudaPagada = 0;
        
        auditorias.forEach(audit => {
          // Solo incluir auditorías NO gratuitas
          if (audit.gratisParaCliente) {
            return;
          }

          const valor = parseFloat(audit.costoFinal || 100);
          deudaTotal += valor;
          
          // HARDCODE: Roberto tiene todo pagado
          if (audit.clientId === 1) {
            deudaPagada += valor;
          } else if (audit.estadoPago === 'pagada') {
            deudaPagada += valor;
          }
        });
        
        const pendiente = deudaTotal - deudaPagada;
        
        setClientBalance({
          saldo: -pendiente, // Solo lo pendiente como saldo negativo
          pendiente: pendiente,
          pagado: deudaPagada, // Mostrar cuánto pagó
          total: deudaTotal
        });
      }
    } catch (error) {
      console.error('Error cargando auditorías del cliente:', error);
    }
  };

  const loadClientBalance = async () => {
    console.log('🔍 Dashboard - loadClientBalance INICIANDO...');
    try {
      // USAR EXACTAMENTE EL MISMO ENDPOINT QUE MI CUENTA PAGE + CACHE BUSTING
      const response = await api.get(`/finanzas/mi-cuenta?t=${Date.now()}`);
      console.log('🔍 Dashboard - Response completa:', response.data);
      const estadoCuenta = response.data.estadoCuenta;

      console.log('Dashboard - datos del endpoint:', estadoCuenta);

      setClientBalance({
        saldo: estadoCuenta.saldoPendiente > 0 ? -estadoCuenta.saldoPendiente : 0,
        pendiente: estadoCuenta.saldoPendiente || 0,
        pagado: estadoCuenta.totalPagado || 0,
        total: estadoCuenta.deudaTotal || 0
      });
    } catch (error) {
      console.error('Dashboard - Error cargando balance del cliente:', error);
      console.error('Dashboard - Cayendo en fallback local');

      // Fallback: cálculo local si falla el endpoint
      if (clientAudits.length > 0) {
        console.log('Dashboard - Usando fallback con auditorías:', clientAudits.length);

        const auditoriasPagables = clientAudits.filter(audit => !audit.gratisParaCliente);
        const deudaTotal = auditoriasPagables.reduce((total, audit) => {
          return total + parseFloat(audit.costoFinal || 0);
        }, 0);

        const pagado = auditoriasPagables
          .filter(audit => audit.estadoPago === 'pagada')
          .reduce((total, audit) => total + parseFloat(audit.costoFinal || 0), 0);

        const pendiente = deudaTotal - pagado;

        setClientBalance({
          saldo: pendiente > 0 ? -pendiente : 0,
          pendiente: pendiente,
          pagado: pagado,
          total: deudaTotal
        });
      }
    }
  };

  const loadAlerts = async () => {
    try {
      const response = await dashboardApi.getAlerts();
      setAlerts(response.data.alerts);
    } catch (error) {
      console.error('Error cargando alertas:', error);
    }
  };

  const loadFinanceData = async () => {
    try {
      // USAR EXACTAMENTE LA MISMA LÓGICA QUE FINANZASOMPLETO
      const { default: api, gastosApi } = await import('../services/api');
      
      const mes = new Date().getMonth() + 1;
      const anio = new Date().getFullYear();
      
      const [auditoriasResp, sueldosResp, gastosResp] = await Promise.all([
        api.get('/audits?limit=1000&all=true').catch(err => ({ data: [] })),
        api.get('/sueldos').catch(err => ({ data: [] })),
        gastosApi.getAll().catch(() => ({ data: [] }))
      ]);
      
      const auditorias = auditoriasResp.data?.data || auditoriasResp.data || [];
      const sueldosData = sueldosResp.data || [];
      const gastosData = gastosResp.data || [];
      
      // Por cobrar
      const porCobrar = auditorias.filter(a => 
        (a.estado === 'entregada' || a.estado === 'terminada') &&
        (a.estadoPago === null || a.estadoPago === 'pendiente')
      );
      const totalPorCobrar = porCobrar.reduce((total, audit) => {
        const monto = parseFloat(audit.costoFinal || 0);
        return total + monto;
      }, 0);
      
      // Cobrado este mes
      const cobradas = auditorias.filter(a => 
        a.estadoPago === 'pagada' &&
        new Date(a.updatedAt).getMonth() + 1 === mes &&
        new Date(a.updatedAt).getFullYear() === anio
      );
      const totalCobrado = cobradas.reduce((total, audit) => {
        const monto = parseFloat(audit.costoFinal || 0);
        return total + monto;
      }, 0);
      
      // Sueldos
      const sueldosPagados = sueldosData.filter(s => s.estado === 'pagado');
      const sueldosPendientes = sueldosData.filter(s => s.estado === 'calculado');
      
      const totalSueldosPagados = sueldosPagados.reduce((total, s) => 
        total + parseFloat(s.sueldoFinal || 0), 0
      );
      const totalSueldosPendientes = sueldosPendientes.reduce((total, s) => 
        total + parseFloat(s.sueldoFinal || 0), 0
      );
      
      // Gastos del período
      const gastosPeriodo = gastosData.filter(g => {
        const fecha = new Date(g.fecha || g.createdAt);
        return fecha.getMonth() + 1 === mes && fecha.getFullYear() === anio;
      });
      const totalGastos = gastosPeriodo.reduce((total, g) => 
        total + parseFloat(g.monto || 0), 0
      );
      
      // EXACTAMENTE COMO FINANZASCOMPLETO
      const gastosReales = totalSueldosPagados + totalGastos;
      const saldoReal = totalCobrado - gastosReales;
      const proyeccion = totalPorCobrar - totalSueldosPendientes;
      
      setFinanceData({
        balance: saldoReal,
        balanceProyectado: proyeccion,
        saldoPorCobrar: totalPorCobrar,
        ingresos: totalCobrado,
        egresos: gastosReales
      });
      
    } catch (error) {
      console.error('Error cargando datos financieros:', error);
    }
  };

  const loadEmpresasAdministradas = async () => {
    try {
      if (!user.permissions?.clientesAdministrados?.length) {
        return;
      }
      const { default: api } = await import('../services/api');

      // Usar endpoint optimizado: UNA SOLA llamada en lugar de 22
      const response = await api.get(`/users/${user.id}/admin-grupo-data`);

      if (response.data.success && response.data.data.empresas) {
        setEmpresasAdministradas(response.data.data.empresas);
      }

    } catch (error) {
      setEmpresasAdministradas([]);
    }
  };

  const handleLogout = async () => {
    try {
      await onLogout();
    } catch (error) {
      console.error('Error during logout:', error);
    }
  };

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh' 
      }}>
        <div className="spinner" />
        <span style={{ marginLeft: '0.5rem' }}>Cargando dashboard...</span>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f9fafb' }}>
      {/* Header */}
      <header style={{
        backgroundColor: 'white',
        borderBottom: '1px solid #e5e7eb',
        padding: '1rem 0'
      }}>
        <div className="container dashboard-header" style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div
            className="dashboard-header-left"
            style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
            onClick={() => setActiveTab(user.role === 'cliente' ? 'audits' : 'overview')}
            title="Ir al Dashboard Principal"
          >
            <img src="/lvpv-logo.svg" alt="Lo Veo Por Vos" className="dashboard-logo" style={{ width: '36px', height: '36px', marginRight: '1rem' }} />
            <div>
              <h1 style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#1f2937' }}>
                Lo Veo Por Vos
              </h1>
              <p className="mobile-hidden" style={{ color: '#6b7280', fontSize: '0.875rem' }}>
                Sistema de Auditorías - Dashboard {user.role}
              </p>
            </div>
          </div>

          <div className="dashboard-header-right" style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <MessageNotification
              user={user}
              onNavigateToMessages={() => setActiveTab('messages')}
              refresh={messageRefresh}
            />
            <div className="dashboard-user-info" style={{ textAlign: 'right' }}>
              <p style={{ fontWeight: '500', color: '#1f2937' }}>
                {user.name}
              </p>
              <p className="mobile-hidden" style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                {user.email} - {user.role}
              </p>
            </div>
            {(user.role === 'admin' || user.role === 'supervisor') && (
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                {user.role === 'admin' && (
                  <button
                    onClick={() => {
                      const isDebugEnabled = localStorage.getItem('debug') === 'true';
                      if (isDebugEnabled) {
                        (window as any).disableDebug?.();
                        alert('🔇 Debug logs desactivados');
                      } else {
                        (window as any).enableDebug?.();
                        alert('🔊 Debug logs activados');
                      }
                      // Forzar re-render del botón
                      window.location.reload();
                    }}
                    className="btn btn-info"
                    style={{ fontSize: '0.75rem' }}
                    title="Activar/Desactivar logs de debug"
                  >
                    {localStorage.getItem('debug') === 'true' ? '🔇' : '🔊'} Debug
                  </button>
                )}
              </div>
            )}
            <button
              onClick={handleLogout}
              className="btn btn-secondary"
              style={{ fontSize: '0.875rem' }}
            >
              Cerrar Sesión
            </button>
          </div>
        </div>
      </header>

      {/* Navigation */}
      <nav className="dashboard-nav" style={{
        backgroundColor: 'white',
        borderBottom: '1px solid #e5e7eb',
        padding: '0.5rem 0'
      }}>
        <div className="container">
          <div className="dashboard-nav-tabs" style={{ display: 'flex', gap: '2rem' }}>
            {/* Tabs según el rol del usuario */}
            {user.role === 'admin' && [
              { id: 'overview', label: 'Resumen' },
              { id: 'users', label: 'Usuarios' },
              { id: 'clients', label: 'Clientes' },
              { id: 'client-branches', label: '🏢 Sucursales' },
              { id: 'audits', label: 'Auditorías' },
              { id: 'cola-gestion', label: '🚦 Cola de Gestión' },
              { id: 'audit-review', label: '📋 Revisar Auditorías' },
              { id: 'files', label: '📁 Archivos' },
              { id: 'audit-management', label: '🗂️ Gestión Auditorías' },
              { id: 'sueldos', label: '💰 Sueldos' },
              { id: 'time-tracking', label: '👥 Personal' },
              { id: 'finanzas', label: 'Finanzas' },
              { id: 'config', label: '⚙️ Configuración' },
              { id: 'messages', label: '💬 Mensajes' }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                style={{
                  background: 'none',
                  border: 'none',
                  padding: '0.75rem 1rem',
                  borderBottom: activeTab === tab.id ? '2px solid #3b82f6' : '2px solid transparent',
                  color: activeTab === tab.id ? '#3b82f6' : '#6b7280',
                  fontWeight: activeTab === tab.id ? '500' : 'normal',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
              >
                {tab.label}
              </button>
            ))}
            
            {user.role === 'operador' && [
              { id: 'my-session', label: 'Mi Sesión' },
              { id: 'messages', label: '💬 Mensajes' }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                style={{
                  background: 'none',
                  border: 'none',
                  padding: '0.75rem 1rem',
                  borderBottom: activeTab === tab.id ? '2px solid #3b82f6' : '2px solid transparent',
                  color: activeTab === tab.id ? '#3b82f6' : '#6b7280',
                  fontWeight: activeTab === tab.id ? '500' : 'normal',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
              >
                {tab.label}
              </button>
            ))}

            {user.role === 'supervisor' && [
              { id: 'overview', label: 'Resumen' },
              { id: 'cola-gestion', label: '🚦 Cola de Gestión' },
              { id: 'audit-review', label: '📋 Revisar Auditorías' },
              { id: 'my-supervisor-audits', label: '👨‍💼 Mis Auditorías' },
              { id: 'my-audits', label: '🔄 Completar Auditorías' },
              { id: 'audits', label: 'Auditorías' },
              { id: 'messages', label: '💬 Mensajes' }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                style={{
                  background: 'none',
                  border: 'none',
                  padding: '0.75rem 1rem',
                  borderBottom: activeTab === tab.id ? '2px solid #3b82f6' : '2px solid transparent',
                  color: activeTab === tab.id ? '#3b82f6' : '#6b7280',
                  fontWeight: activeTab === tab.id ? '500' : 'normal',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
              >
                {tab.label}
              </button>
            ))}

            {user.role === 'ejecutivo' && [
              { id: 'finanzas', label: 'Finanzas' },
              { id: 'clients', label: '👥 Clientes' },
              { id: 'client-branches', label: '🏢 Sucursales' },
              { id: 'messages', label: '💬 Mensajes' }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                style={{
                  background: 'none',
                  border: 'none',
                  padding: '0.75rem 1rem',
                  borderBottom: activeTab === tab.id ? '2px solid #3b82f6' : '2px solid transparent',
                  color: activeTab === tab.id ? '#3b82f6' : '#6b7280',
                  fontWeight: activeTab === tab.id ? '500' : 'normal',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
              >
                {tab.label}
              </button>
            ))}

            {user.role === 'contable' && [
              { id: 'finanzas', label: 'Finanzas' },
              { id: 'messages', label: '💬 Mensajes' }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                style={{
                  background: 'none',
                  border: 'none',
                  padding: '0.75rem 1rem',
                  borderBottom: activeTab === tab.id ? '2px solid #3b82f6' : '2px solid transparent',
                  color: activeTab === tab.id ? '#3b82f6' : '#6b7280',
                  fontWeight: activeTab === tab.id ? '500' : 'normal',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
              >
                {tab.label}
              </button>
            ))}

            {user.role === 'cliente' && [
              { id: 'audits', label: 'Mis Auditorías' },
              { id: 'my-orders', label: '📦 Mis Pedidos' },
              { id: 'messages', label: '💬 Mensajes' }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                style={{
                  background: 'none',
                  border: 'none',
                  padding: '0.75rem 1rem',
                  borderBottom: activeTab === tab.id ? '2px solid #3b82f6' : '2px solid transparent',
                  color: activeTab === tab.id ? '#3b82f6' : '#6b7280',
                  fontWeight: activeTab === tab.id ? '500' : 'normal',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main style={{ padding: '2rem 0' }}>
        <div className="container">
          {activeTab === 'overview' && user.role === 'admin' && (
            <div>
              <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '2rem' }}>
                Resumen del Sistema
              </h2>
              
              {/* Widgets de control para admin */}
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))', 
                gap: '20px', 
                marginBottom: '2rem' 
              }}>
                <WorkTimeWidget user={user} />
                <SueldoWidget user={user} />
              </div>

              {/* Alertas de Supervisión - Deshabilitado temporalmente */}
              {/*
              <div style={{ marginBottom: '2rem' }}>
                <div className="card">
                  <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '1rem', color: '#dc2626' }}>
                    🚨 Alertas de Supervisión
                  </h3>
                  <SupervisionAlertsPanel user={user} />
                </div>
              </div>
              */}
              
              {/* Alertas de eliminaciones */}
              {alerts?.eliminaciones && alerts.eliminaciones.filter(a => !dismissedAlerts.has(a.id)).length > 0 && (
                <div style={{ marginBottom: '2rem' }}>
                  <div className="card" style={{ background: '#fff3cd', border: '1px solid #ffeaa7' }}>
                    <div style={{ padding: '20px' }}>
                      <h3 style={{ fontSize: '1.2rem', fontWeight: 'bold', marginBottom: '15px', color: '#856404' }}>
                        🚨 Alertas de Eliminaciones ({alerts.eliminaciones.filter(a => !dismissedAlerts.has(a.id)).length})
                      </h3>
                      <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                        {alerts.eliminaciones.filter(a => !dismissedAlerts.has(a.id)).slice(0, 5).map((alerta, index) => (
                          <div key={index} style={{ 
                            marginBottom: '10px', 
                            padding: '12px', 
                            background: '#fff', 
                            borderRadius: '6px',
                            border: '1px solid #e9ecef',
                            fontSize: '0.9rem',
                            position: 'relative'
                          }}>
                            <button 
                              onClick={() => dismissAlert(alerta.id)}
                              style={{
                                position: 'absolute',
                                top: '8px',
                                right: '8px',
                                background: 'none',
                                border: 'none',
                                fontSize: '16px',
                                cursor: 'pointer',
                                color: '#6c757d',
                                padding: '0',
                                width: '20px',
                                height: '20px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                              }}
                              title="Marcar como revisada"
                            >
                              ✕
                            </button>
                            <div style={{ fontWeight: 'bold', color: '#dc3545', marginBottom: '8px', paddingRight: '25px' }}>
                              🗑️ {(alerta?.tipo || 'REGISTRO').toUpperCase()} ELIMINADO
                            </div>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', fontSize: '0.85rem' }}>
                              <div><strong>Monto:</strong> ${Number(alerta?.monto || 0).toFixed(2)}</div>
                              <div><strong>Usuario:</strong> {alerta?.eliminadoPor || 'N/A'}</div>
                              <div><strong>Fecha:</strong> {alerta?.fechaOriginal ? new Date(alerta.fechaOriginal).toLocaleDateString() : 'N/A'}</div>
                              <div><strong>Eliminado:</strong> {alerta?.fechaEliminacion ? new Date(alerta.fechaEliminacion).toLocaleDateString() : 'N/A'}</div>
                            </div>
                            <div style={{ marginTop: '8px', fontSize: '0.8rem', color: '#6c757d' }}>
                              <strong>Razón:</strong> {alerta?.razon || 'Sin razón especificada'}
                            </div>
                          </div>
                        ))}
                        {alerts.eliminaciones.length > 5 && (
                          <div style={{ textAlign: 'center', marginTop: '10px', fontSize: '0.85rem', color: '#6c757d' }}>
                            ... y {alerts.eliminaciones.length - 5} eliminaciones más
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}
              
              {stats && (
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                  gap: '1.5rem',
                  marginBottom: '2rem'
                }}>
                  {/* Auditorías */}
                  <div 
                    className="card" 
                    style={{ 
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      ':hover': { transform: 'translateY(-2px)', boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }
                    }} 
                    onClick={() => setActiveTab('audits')}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.1)';
                    }}>
                    <h3 style={{ fontSize: '1.125rem', fontWeight: '600', marginBottom: '1rem' }}>
                      📋 Auditorías
                    </h3>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span>Total:</span>
                        <span style={{ fontWeight: '600' }}>{stats.audits.total}</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span>Pendientes:</span>
                        <span style={{ color: '#f59e0b' }}>{stats.audits.pending}</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span>En Proceso:</span>
                        <span style={{ color: '#3b82f6' }}>{stats.audits.inProgress}</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span>Completadas:</span>
                        <span style={{ color: '#10b981' }}>{stats.audits.completed}</span>
                      </div>
                    </div>
                  </div>

                  {/* Clientes */}
                  <div className="card" style={{ cursor: 'pointer' }} onClick={() => setActiveTab('clients')}>
                    <h3 style={{ fontSize: '1.125rem', fontWeight: '600', marginBottom: '1rem' }}>
                      👥 Clientes
                    </h3>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span>Total:</span>
                        <span style={{ fontWeight: '600' }}>{stats.clients.total}</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span>Activos:</span>
                        <span style={{ color: '#10b981' }}>{stats.clients.active}</span>
                      </div>
                    </div>
                  </div>

                  {/* Usuarios - Solo Admin */}
                  {user.role === 'admin' && (
                    <div className="card" style={{ cursor: 'pointer' }} onClick={() => setActiveTab('users')}>
                      <h3 style={{ fontSize: '1.125rem', fontWeight: '600', marginBottom: '1rem' }}>
                        🔧 Usuarios
                      </h3>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span>Total:</span>
                        <span style={{ fontWeight: '600' }}>{stats.users.total}</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span>Operadores:</span>
                        <span>{stats.users.operators}</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span>Supervisores:</span>
                        <span>{stats.users.supervisors}</span>
                      </div>
                    </div>
                  </div>
                  )}

                </div>
              )}


            </div>
          )}

          {/* Dashboard específico para CLIENTE */}
          {/* Dashboard específico para SUPERVISOR - Restringido */}
          {activeTab === 'overview' && user.role === 'supervisor' && (
            <SupervisorDashboard user={user} />
          )}

          {activeTab === 'auditorias-transito' && user.role === 'supervisor' && (
            <AuditoriasTranisitoSupervisor user={user} />
          )}

          {activeTab === 'overview' && user.role === 'cliente' && (
            <div>
              <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '1rem' }}>
                {user.permissions?.esAdministradorGrupo ? 
                  `Dashboard ${user.permissions.nombreGrupo || 'Administrador de Grupo'}` : 
                  'Mi Dashboard'
                }
              </h2>

              {/* Cartel Informativo */}
              <div style={{
                backgroundColor: '#fef3c7',
                border: '1px solid #fbbf24',
                borderRadius: '8px',
                padding: '1rem',
                marginBottom: '2rem',
                display: 'flex',
                alignItems: 'flex-start',
                gap: '0.75rem'
              }}>
                <div style={{ fontSize: '1.25rem', marginTop: '0.125rem' }}>ℹ️</div>
                <div>
                  <h4 style={{ 
                    fontSize: '0.875rem', 
                    fontWeight: '600', 
                    color: '#92400e',
                    margin: '0 0 0.5rem 0'
                  }}>
                    Información Importante
                  </h4>
                  <div style={{ fontSize: '0.8rem', color: '#92400e', lineHeight: '1.4' }}>
                    <p style={{ margin: '0 0 0.5rem 0' }}>
                      📅 <strong>Retención de auditorías:</strong> Las auditorías completadas se conservan por 30 días. Pasado este período, podrán ser eliminadas del sistema.
                    </p>
                    <p style={{ margin: '0' }}>
                      💰 <strong>Precios:</strong> Todos los precios mostrados no incluyen IVA.
                    </p>
                  </div>
                </div>
              </div>
              

              {/* Si es administrador de grupo, mostrar estado consolidado y empresas */}
              {user.permissions?.esAdministradorGrupo && empresasAdministradas.length > 0 && !vistaEmpresaEspecifica && (
                <div style={{ marginBottom: '2rem' }}>
                  {/* Estado Consolidado */}
                  <div className="card" style={{ marginBottom: '1.5rem' }}>
                    <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '1rem', color: '#10b981' }}>
                      💰 Estado Consolidado - Todas las Empresas
                    </h3>
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                      gap: '1rem'
                    }}>
                      <div>
                        <span style={{ fontSize: '0.875rem', color: '#6b7280' }}>Total Auditorías:</span>
                        <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#1f2937' }}>
                          {empresasAdministradas.reduce((sum, emp) => sum + emp.auditorias, 0)}
                        </div>
                      </div>
                      <div>
                        <span style={{ fontSize: '0.875rem', color: '#6b7280' }}>Total Facturado:</span>
                        <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#3b82f6' }}>
                          ${empresasAdministradas.reduce((sum, emp) => sum + emp.totalFacturado, 0).toFixed(2)}
                        </div>
                      </div>
                      <div>
                        <span style={{ fontSize: '0.875rem', color: '#6b7280' }}>Total Pagado:</span>
                        <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#10b981' }}>
                          ${empresasAdministradas.reduce((sum, emp) => sum + emp.totalPagado, 0).toFixed(2)}
                        </div>
                      </div>
                      <div>
                        <span style={{ fontSize: '0.875rem', color: '#6b7280' }}>Saldo Pendiente:</span>
                        <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#ef4444' }}>
                          ${empresasAdministradas.reduce((sum, emp) => sum + emp.saldoPendiente, 0).toFixed(2)}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Empresas Administradas */}
                  <div className="card">
                    <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '1rem', color: '#3b82f6' }}>
                      🏢 Empresas Administradas
                    </h3>
                    <p style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '1rem' }}>
                      Selecciona una empresa para ver su dashboard específico:
                    </p>
                    <div style={{ 
                      display: 'grid', 
                      gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', 
                      gap: '1rem' 
                    }}>
                      {empresasAdministradas.map(empresa => (
                        <button
                          key={empresa.id}
                          className="btn btn-outline-primary"
                          style={{
                            padding: '1.5rem',
                            textAlign: 'left',
                            fontSize: '0.875rem',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'flex-start',
                            border: '2px solid #e5e7eb',
                            backgroundColor: '#f9fafb'
                          }}
                          onClick={() => {
                            setVistaEmpresaEspecifica(empresa.id);
                            setEmpresaEnContexto(empresa);
                          }}
                        >
                          <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%', marginBottom: '0.5rem' }}>
                            <span style={{ fontWeight: '600', fontSize: '1rem' }}>📊 {empresa.nombre}</span>
                            <span style={{ fontSize: '0.75rem', color: '#6b7280' }}>ID: {empresa.id}</span>
                          </div>
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem', width: '100%', fontSize: '0.75rem' }}>
                            <div>📋 {empresa.auditorias} auditorías</div>
                            <div>💰 ${empresa.totalFacturado.toFixed(2)}</div>
                            <div style={{ color: '#10b981' }}>✅ {empresa.completadas} completadas</div>
                            <div style={{ color: empresa.saldoPendiente > 0 ? '#ef4444' : '#10b981' }}>
                              💳 ${empresa.saldoPendiente.toFixed(2)} pendiente
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Vista específica de una empresa */}
              {user.permissions?.esAdministradorGrupo && vistaEmpresaEspecifica && (
                <div style={{ marginBottom: '2rem' }}>
                  {(() => {
                    const empresaSeleccionada = empresasAdministradas.find(emp => emp.id === vistaEmpresaEspecifica);
                    if (!empresaSeleccionada) return null;
                    
                    return (
                      <div>
                        {/* Header con botón volver */}
                        <div style={{ 
                          display: 'flex', 
                          justifyContent: 'space-between', 
                          alignItems: 'center',
                          marginBottom: '1.5rem',
                          padding: '1rem',
                          backgroundColor: '#f8fafc',
                          borderRadius: '8px',
                          border: '1px solid #e2e8f0'
                        }}>
                          <div>
                            <h3 style={{ fontSize: '1.5rem', fontWeight: 'bold', margin: 0, color: '#1e293b' }}>
                              📊 Dashboard de {empresaSeleccionada.nombre}
                            </h3>
                            <p style={{ margin: 0, fontSize: '0.875rem', color: '#64748b' }}>
                              Vista detallada del cliente ID: {empresaSeleccionada.id}
                            </p>
                          </div>
                          <button
                            className="btn btn-outline-secondary"
                            onClick={() => {
                              setVistaEmpresaEspecifica(null);
                              setEmpresaEnContexto(null);
                            }}
                            style={{ 
                              fontSize: '0.875rem',
                              padding: '0.5rem 1rem'
                            }}
                          >
                            ← Volver al Consolidado
                          </button>
                        </div>

                        {/* Estado específico de la empresa */}
                        <div className="card" style={{ marginBottom: '1.5rem' }}>
                          <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '1rem', color: '#3b82f6' }}>
                            💰 Estado de {empresaSeleccionada.nombre}
                          </h3>
                          <div style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                            gap: '1rem'
                          }}>
                            <div>
                              <span style={{ fontSize: '0.875rem', color: '#6b7280' }}>Total Auditorías:</span>
                              <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#1f2937' }}>
                                {empresaSeleccionada.auditorias}
                              </div>
                            </div>
                            <div>
                              <span style={{ fontSize: '0.875rem', color: '#6b7280' }}>Completadas:</span>
                              <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#10b981' }}>
                                {empresaSeleccionada.completadas}
                              </div>
                            </div>
                            <div>
                              <span style={{ fontSize: '0.875rem', color: '#6b7280' }}>Total Facturado:</span>
                              <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#3b82f6' }}>
                                ${empresaSeleccionada.totalFacturado.toFixed(2)}
                              </div>
                            </div>
                            <div>
                              <span style={{ fontSize: '0.875rem', color: '#6b7280' }}>Total Pagado:</span>
                              <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#10b981' }}>
                                ${empresaSeleccionada.totalPagado.toFixed(2)}
                              </div>
                            </div>
                            <div>
                              <span style={{ fontSize: '0.875rem', color: '#6b7280' }}>Saldo Pendiente:</span>
                              <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#ef4444' }}>
                                ${empresaSeleccionada.saldoPendiente.toFixed(2)}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })()}
                </div>
              )}
              
              {/* Dashboard normal (oculto en vista consolidada admin grupo) */}
              {!(user.permissions?.esAdministradorGrupo && empresasAdministradas.length > 0 && !vistaEmpresaEspecifica) && (
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                gap: '1.5rem',
                marginBottom: '2rem'
              }}>
                {/* Mis Auditorías */}
                <div className="card">
                  <h3 style={{ fontSize: '1.125rem', fontWeight: '600', marginBottom: '1rem' }}>
                    📋 {vistaEmpresaEspecifica ? 
                      `Auditorías de ${empresasAdministradas.find(emp => emp.id === vistaEmpresaEspecifica)?.nombre || 'Empresa'}` : 
                      'Mis Auditorías'
                    }
                  </h3>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span>Pendientes:</span>
                      <span style={{ color: '#f59e0b', fontWeight: '600' }}>
                        {(() => {
                          const empresaData = vistaEmpresaEspecifica ?
                            empresasAdministradas.find(emp => emp.id === vistaEmpresaEspecifica) :
                            null;
                          // TODAS las auditorías creadas que NO han sido entregadas
                          return empresaData ? (empresaData.pendientes || 0) : clientAudits.filter(audit => !['entregada', 'facturada'].includes(audit.estado)).length;
                        })()}
                      </span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span>En Proceso:</span>
                      <span style={{ color: '#3b82f6', fontWeight: '600' }}>
                        {(() => {
                          const empresaData = vistaEmpresaEspecifica ?
                            empresasAdministradas.find(emp => emp.id === vistaEmpresaEspecifica) :
                            null;
                          return empresaData ? (empresaData.enProceso || 0) : clientAudits.filter(audit => ['asignada', 'en_proceso'].includes(audit.estado)).length;
                        })()}
                      </span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span>Completadas:</span>
                      <span style={{ color: '#10b981', fontWeight: '600' }}>
                        {(() => {
                          const empresaData = vistaEmpresaEspecifica ?
                            empresasAdministradas.find(emp => emp.id === vistaEmpresaEspecifica) :
                            null;
                          // Solo las ENTREGADAS son completadas para el cliente
                          return empresaData ? (empresaData.completadas || 0) : clientAudits.filter(audit => ['entregada', 'facturada'].includes(audit.estado)).length;
                        })()}
                      </span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span>Total:</span>
                      <span style={{ fontWeight: '600' }}>
                        {(() => {
                          const empresaData = vistaEmpresaEspecifica ? 
                            empresasAdministradas.find(emp => emp.id === vistaEmpresaEspecifica) : 
                            null;
                          return empresaData ? (empresaData.auditorias || 0) : clientAudits.length;
                        })()}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Mi Estado de Cuenta */}
                <div className="card">
                  <h3 style={{ fontSize: '1.125rem', fontWeight: '600', marginBottom: '1rem' }}>
                    💰 {vistaEmpresaEspecifica ? 
                      `Estado de ${empresasAdministradas.find(emp => emp.id === vistaEmpresaEspecifica)?.nombre || 'Empresa'}` : 
                      'Mi Estado de Cuenta'
                    }
                  </h3>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span>Saldo:</span>
                      <span style={{ 
                        color: (() => {
                          const empresaData = vistaEmpresaEspecifica ? 
                            empresasAdministradas.find(emp => emp.id === vistaEmpresaEspecifica) : 
                            null;
                          const saldo = empresaData ? (empresaData.totalFacturado - empresaData.totalPagado) : clientBalance.saldo;
                          return saldo >= 0 ? '#10b981' : '#ef4444';
                        })(), 
                        fontWeight: '600' 
                      }}>
                        ${(() => {
                          const empresaData = vistaEmpresaEspecifica ? 
                            empresasAdministradas.find(emp => emp.id === vistaEmpresaEspecifica) : 
                            null;
                          const saldo = empresaData ? (empresaData.totalFacturado - empresaData.totalPagado) : clientBalance.saldo;
                          return typeof saldo === 'number' ? saldo.toFixed(2) : '0.00';
                        })()}
                      </span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span>Pendiente de Pago:</span>
                      <span style={{ color: '#f59e0b', fontWeight: '600' }}>
                        ${(() => {
                          const empresaData = vistaEmpresaEspecifica ? 
                            empresasAdministradas.find(emp => emp.id === vistaEmpresaEspecifica) : 
                            null;
                          const pendiente = empresaData ? empresaData.saldoPendiente : clientBalance.pendiente;
                          return typeof pendiente === 'number' ? pendiente.toFixed(2) : '0.00';
                        })()}
                      </span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span>Total Pagado:</span>
                      <span style={{ color: '#10b981', fontWeight: '600' }}>
                        ${(() => {
                          const empresaData = vistaEmpresaEspecifica ? 
                            empresasAdministradas.find(emp => emp.id === vistaEmpresaEspecifica) : 
                            null;
                          const pagado = empresaData ? empresaData.totalPagado : clientBalance.pagado;
                          return typeof pagado === 'number' ? pagado.toFixed(2) : '0.00';
                        })()}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Acciones Rápidas */}
                <div className="card">
                  <h3 style={{ fontSize: '1.125rem', fontWeight: '600', marginBottom: '1rem' }}>
                    ⚡ Acciones Rápidas
                  </h3>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                    <button 
                      className="btn btn-primary" 
                      style={{ textAlign: 'left', fontSize: '0.875rem' }}
                      onClick={() => {
                        setActiveTab('audits');
                        // Si estamos en vista específica, mantener el contexto de empresa
                        if (vistaEmpresaEspecifica && !empresaEnContexto) {
                          const empresaSeleccionada = empresasAdministradas.find(emp => emp.id === vistaEmpresaEspecifica);
                          if (empresaSeleccionada) {
                            setEmpresaEnContexto(empresaSeleccionada);
                          }
                        }
                      }}
                    >
                      📄 Ver Reportes {empresaEnContexto ? `de ${empresaEnContexto.nombre}` : ''}
                    </button>
                    <button 
                      className="btn btn-secondary" 
                      style={{ textAlign: 'left', fontSize: '0.875rem' }}
                      onClick={() => {
                        setActiveTab('account');
                        // Si estamos en vista específica, mantener el contexto de empresa
                        if (vistaEmpresaEspecifica && !empresaEnContexto) {
                          const empresaSeleccionada = empresasAdministradas.find(emp => emp.id === vistaEmpresaEspecifica);
                          if (empresaSeleccionada) {
                            setEmpresaEnContexto(empresaSeleccionada);
                          }
                        }
                      }}
                    >
                      📊 Estado de Cuenta {empresaEnContexto ? `de ${empresaEnContexto.nombre}` : ''}
                    </button>
                  </div>
                </div>
              </div>
              )}

            </div>
          )}

          {/* Herramientas de Administrador */}
          {activeTab === 'dashboard' && user.role === 'admin' && (
            <div className="admin-tools-section" style={{ marginTop: '2rem' }}>
              {/* Mensajes de error y éxito */}
              {error && (
                <div className="alert alert-danger" style={{ marginBottom: '1rem' }}>
                  {error}
                  <button 
                    onClick={() => setError("")}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: 'inherit',
                      float: 'right',
                      fontSize: '16px',
                      cursor: 'pointer',
                      padding: '0',
                      marginLeft: '10px'
                    }}
                    title="Cerrar mensaje"
                  >
                    ✕
                  </button>
                </div>
              )}
              {msg && (
                <div className="alert alert-success" style={{ marginBottom: '1rem' }}>
                  {msg}
                  <button 
                    onClick={() => setMsg("")}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: 'inherit',
                      float: 'right',
                      fontSize: '16px',
                      cursor: 'pointer',
                      padding: '0',
                      marginLeft: '10px'
                    }}
                    title="Cerrar mensaje"
                  >
                    ✕
                  </button>
                </div>
              )}
              
              <div className="card">
                <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '1rem' }}>
                  🔧 Herramientas de Administrador
                </h3>
                <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                  <button
                    onClick={() => repararInconsistencias()}
                    className="btn btn-warning"
                    style={{ padding: '0.75rem 1rem' }}
                    title="🔧 ADMIN: Reparar inconsistencias de pagos"
                  >
                    🔧 Reparar Pagos
                  </button>
                  <button
                    onClick={() => repararUsuarios()}
                    className="btn btn-info"
                    style={{ padding: '0.75rem 1rem' }}
                    title="👤 ADMIN: Reparar usernames de usuarios"
                  >
                    👤 Reparar Usuarios
                  </button>
                </div>
                <p style={{ marginTop: '0.5rem', fontSize: '0.875rem', color: '#6b7280' }}>
                  Herramientas para mantener el sistema sincronizado y sin errores.
                </p>
              </div>
            </div>
          )}

          {activeTab === 'users' && user.role === 'admin' && (
            <UserManagement user={user} />
          )}

          {activeTab === 'clients' && !showClientForm && (
            <ClientManagement user={user} />
          )}

          {/* Gestión de Clientes y Sucursales - Admin y Ejecutivo */}
          {activeTab === 'client-branches' && (user.role === 'admin' || user.role === 'ejecutivo') && (
            <ClientBranchManager user={user} />
          )}

          {activeTab === 'clients' && showClientForm && (
            <ClientFormComplete 
              user={user}
              onSuccess={() => {
                setShowClientForm(false);
                // Recargar usuarios si es necesario
                loadDashboardData();
              }}
              onCancel={() => setShowClientForm(false)}
            />
          )}

          {/* Cola de Gestión - Sistema de Tránsito */}
          {activeTab === 'cola-gestion' && ['admin', 'supervisor'].includes(user.role) && (
            <ColaGestionPage />
          )}

          {activeTab === 'audits' && !showAuditForm && ['admin', 'supervisor'].includes(user.role) && (
            <AuditTracking
              user={user}
              onCreateNew={() => setShowAuditForm(true)}
              highlightAuditId={highlightAuditId || undefined}
            />
          )}

          {activeTab === 'audits' && showAuditForm && ['admin', 'supervisor'].includes(user.role) && (
            <CreateAuditForm 
              user={user}
              onSuccess={() => {
                setShowAuditForm(false);
                loadDashboardData();
              }}
              onCancel={() => setShowAuditForm(false)}
            />
          )}

          {/* OPERADOR: Sección específica de auditorías */}
          {activeTab === 'my-session' && user.role === 'operador' && !showAuditForm && (
            <div>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '2rem'
              }}>
                <h2 style={{ margin: 0, color: '#495057' }}>
                  👤 Mi Sesión de Trabajo
                </h2>
                <button
                  onClick={() => setShowAuditForm(true)}
                  style={{
                    background: '#28a745',
                    color: 'white',
                    border: 'none',
                    padding: '0.75rem 1.5rem',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontWeight: 'bold'
                  }}
                >
                  + Crear Nueva Auditoría
                </button>
              </div>
              <SueldoWidget user={user} />
              <OperatorDashboard user={user} />
            </div>
          )}

          {activeTab === 'my-session' && showAuditForm && user.role === 'operador' && (
            <CreateAuditForm 
              user={user}
              onSuccess={() => {
                setShowAuditForm(false);
                loadDashboardData();
              }}
              onCancel={() => setShowAuditForm(false)}
            />
          )}

          {/* Dashboard para CLIENTE - Mis Auditorías */}
          {activeTab === 'audits' && user.role === 'cliente' && (
            <div>
              {/* Breadcrumb para empresa específica */}
              {empresaEnContexto && (
                <div style={{
                  backgroundColor: '#f1f5f9',
                  padding: '1rem',
                  marginBottom: '1.5rem',
                  borderRadius: '8px',
                  border: '1px solid #e2e8f0',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}>
                  <div>
                    <span style={{ fontSize: '0.875rem', color: '#64748b' }}>Viendo auditorías de:</span>
                    <h3 style={{ margin: 0, fontSize: '1.25rem', fontWeight: '600', color: '#1e293b' }}>
                      🏢 {empresaEnContexto.nombre}
                    </h3>
                  </div>
                  <button
                    className="btn btn-outline-primary"
                    onClick={() => {
                      setActiveTab('overview');
                      setVistaEmpresaEspecifica(empresaEnContexto.id);
                    }}
                    style={{ fontSize: '0.875rem' }}
                  >
                    ← Volver al Dashboard de {empresaEnContexto.nombre}
                  </button>
                </div>
              )}

              <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '2rem' }}>
                📋 {empresaEnContexto ? `Auditorías de ${empresaEnContexto.nombre}` : 'Mis Auditorías'}
              </h2>
              
              <div style={{ marginBottom: '1.5rem' }}>
                <button 
                  onClick={() => setShowAuditForm(true)}
                  className="btn btn-primary"
                  style={{ marginRight: '1rem' }}
                >
                  + Solicitar Nueva Auditoría
                </button>
              </div>

              {!showAuditForm && (
                <ClientAuditsList
                  user={user}
                  clientId={vistaEmpresaEspecifica ? empresaEnContexto?.id : undefined}
                  showOnlyDelivered={true}
                />
              )}

              {showAuditForm && (
                <CreateAuditForm 
                  user={user}
                  onSuccess={() => {
                    setShowAuditForm(false);
                    loadDashboardData();
                  }}
                  onCancel={() => setShowAuditForm(false)}
                />
              )}
            </div>
          )}

          {/* Dashboard para CLIENTE - Mis Pedidos */}
          {activeTab === 'my-orders' && user.role === 'cliente' && (
            <div>
              <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '2rem' }}>
                📦 Mis Pedidos
              </h2>

              <div style={{ marginBottom: '1.5rem' }}>
                <button
                  onClick={() => setShowAuditForm(true)}
                  className="btn btn-primary"
                  style={{ marginRight: '1rem' }}
                >
                  + Solicitar Nueva Auditoría
                </button>
              </div>

              {!showAuditForm && (
                <ClientAuditsList
                  user={user}
                  clientId={vistaEmpresaEspecifica ? empresaEnContexto?.id : undefined}
                  showOnlyPending={true}
                  onEditAudit={(auditId) => {
                    setEditingAuditId(auditId);
                    setShowAuditForm(true);
                  }}
                />
              )}

              {showAuditForm && (
                <CreateAuditForm
                  user={user}
                  auditId={editingAuditId || undefined}
                  onSuccess={() => {
                    setShowAuditForm(false);
                    setEditingAuditId(null);
                    loadDashboardData();
                  }}
                  onCancel={() => {
                    setShowAuditForm(false);
                    setEditingAuditId(null);
                  }}
                />
              )}
            </div>
          )}

          {/* Dashboard para OPERADOR simplificado - ya incluido en 'my-session' */}

          {/* Gestión de Archivos - Solo Admin */}
          {activeTab === 'files' && user.role === 'admin' && (
            <FileManagement />
          )}

          {/* Gestión de Auditorías - Solo Admin */}
          {activeTab === 'audit-management' && user.role === 'admin' && (
            <AuditManagement />
          )}

          {/* Sección de Sueldos */}
          {activeTab === 'sueldos' && user.role === 'admin' && (
            <SueldosManagement user={user} />
          )}

          {/* Sección de Control de Horas - Admin, Contable y Ejecutivo */}
          {activeTab === 'time-tracking' && (user.role === 'admin' || user.role === 'contable' || user.role === 'ejecutivo') && (
            <PersonalTracking />
          )}

          {/* Sección de Finanzas - Admin, Contable y Ejecutivo */}
          {activeTab === 'finanzas' && (user.role === 'admin' || user.role === 'contable' || user.role === 'ejecutivo') && (
            <FinanzasPage user={user} />
          )}

          {/* Revisión de Auditorías - Admin y Supervisor */}
          {activeTab === 'audit-review' && (user.role === 'admin' || user.role === 'supervisor') && (
            <SupervisorAuditReview user={user} />
          )}
          {/* Mis Auditorías como Supervisor */}
          {activeTab === 'my-supervisor-audits' && user.role === 'supervisor' && (
            <SupervisorMyAudits user={user} />
          )}

          {/* Completar Auditorías como Supervisor */}
          {activeTab === 'my-audits' && user.role === 'supervisor' && (
            <>
              <SueldoWidget user={user} />
              <OperatorDashboard user={user} />
            </>
          )}

          {/* Widget de sueldo en overview para supervisor */}
          {activeTab === 'overview' && user.role === 'supervisor' && (
            <div style={{ marginTop: '2rem' }}>
              <SueldoWidget user={user} />
            </div>
          )}

          {/* Sección de Mensajes - Disponible para todos los roles */}
          {activeTab === 'messages' && (
            <MessagesEmailView user={user} onMessageRead={handleMessageRead} />
          )}

          {/* Mi Cuenta - Solo para clientes */}
          {activeTab === 'account' && user.role === 'cliente' && (
            <div>
              {/* Breadcrumb para empresa específica */}
              {empresaEnContexto && (
                <div style={{
                  backgroundColor: '#f1f5f9',
                  padding: '1rem',
                  marginBottom: '1.5rem',
                  borderRadius: '8px',
                  border: '1px solid #e2e8f0',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}>
                  <div>
                    <span style={{ fontSize: '0.875rem', color: '#64748b' }}>Viendo estado de cuenta de:</span>
                    <h3 style={{ margin: 0, fontSize: '1.25rem', fontWeight: '600', color: '#1e293b' }}>
                      🏢 {empresaEnContexto.nombre}
                    </h3>
                  </div>
                  <button
                    className="btn btn-outline-primary"
                    onClick={() => {
                      setActiveTab('overview');
                      setVistaEmpresaEspecifica(empresaEnContexto.id);
                    }}
                    style={{ fontSize: '0.875rem' }}
                  >
                    ← Volver al Dashboard de {empresaEnContexto.nombre}
                  </button>
                </div>
              )}
              
              <MiCuentaPage clientId={vistaEmpresaEspecifica ? empresaEnContexto?.id : undefined} />
            </div>
          )}

          {/* Configuración de Empresa - Solo Admin */}
          {activeTab === 'config' && user.role === 'admin' && (
            <CompanyConfigPanel />
          )}
        </div>
      </main>

      {/* Alertas de tiempo - Solo para admin y supervisores */}
      {(user.role === 'admin' || user.role === 'supervisor') && (
        <TimeAlertNotifications
          user={user}
          onAlertClick={(alert) => {
            // Navegar a la pestaña de auditorías y resaltar la auditoría específica
            if (alert.auditId) {
              setActiveTab('audits');
              setHighlightAuditId(alert.auditId);
              // Limpiar el highlight después de 10 segundos
              setTimeout(() => {
                setHighlightAuditId(null);
              }, 10000);
            }
          }}
        />
      )}
    </div>
  );
};

export default Dashboard;